import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { filterSportDataByGameStartTsRange } from 'utils/sportsbook/filterSportDataByGameStartTsRange';
import { AdvancedSportsContent } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedSportsContent';
import { ADVANCED_LEFT_MENU_SHOW_EVENTS_BUTTON_ID } from 'newelements/Sportsbook/layouts/advanced/Desktop/index';
import { useGetAdvancedSportsMainData } from 'newelements/Sportsbook/layouts/advanced/helpers/useGetAdvancedSportsMainData';
import { RegionSkeleton } from 'components/UI/Skeletons/RegionSkeleton';
import { useGetUserSportOrder } from 'hooks/sportsbook/useGetUserSportOrder';
import { setAdvancedTempCompetitionIds } from 'store/actions/new-sportsbook';
import {
  getAdvancedSelectedRangeFilter,
  getAdvancedTempCompetitionIds,
  getSportsbookSelectedSport,
  getSwarmResponse
} from 'store/selectors/new-sportsbook';
import './index.less';

export const ADVANCED_LEFT_MENU_DATA_REDUX_KEY = 'advancedAllSports';

export const AdvancedLeftMenu = (): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const mainSportsMenuSelectedSport = useSelector(getSportsbookSelectedSport);
  const selectedCompetitionIds = useSelector(getAdvancedTempCompetitionIds);
  const selectedRange = useSelector(getAdvancedSelectedRangeFilter);
  const sportsData =
    useSelector(getSwarmResponse)?.[ADVANCED_LEFT_MENU_DATA_REDUX_KEY];

  const { orderSports } = useGetUserSportOrder();

  useGetAdvancedSportsMainData();

  const orderedSports = useMemo(() => {
    if (sportsData) {
      let filteredData = sportsData;

      if (mainSportsMenuSelectedSport?.id && selectedRange?.ranges) {
        const selectedSportData = sportsData[mainSportsMenuSelectedSport.id];
        const currentSportFilteredData = filterSportDataByGameStartTsRange(
          { [mainSportsMenuSelectedSport.id]: selectedSportData },
          selectedRange.ranges
        );

        filteredData = {
          ...filteredData,
          ...currentSportFilteredData
        };
      }

      const sportsOrderedData = sortByOrder(Object.values(filteredData));

      return orderSports(sportsOrderedData);
    }

    return null;
  }, [
    sportsData,
    orderSports,
    mainSportsMenuSelectedSport?.id,
    selectedRange?.ranges
  ]);

  const onClearAllClick = () => {
    dispatch(setAdvancedTempCompetitionIds([]));
  };

  return (
    <div className="advancedLeftMenu">
      <div className="advancedLeftMenu__title">
        {t('sportsbook.advancedMenu')}
        {selectedCompetitionIds.length ? (
          <span
            onClick={onClearAllClick}
            className="advancedLeftMenu__title__clearAll"
          >
            {t('sportsbook.clearAll')}
          </span>
        ) : null}
      </div>
      {orderedSports ? (
        <AdvancedSportsContent
          sportsData={orderedSports}
          portalRootId={ADVANCED_LEFT_MENU_SHOW_EVENTS_BUTTON_ID}
        />
      ) : (
        <RegionSkeleton />
      )}
    </div>
  );
};
